import { useState } from 'react'
import { ChevronDown, ChevronRight, Plus, Trash2 } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { MultiSelect } from '@/components/ui/multi-select'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/utils/cn'
import { useCltWizardStore } from '@/stores/useCltWizardStore'
import {
  sortMilestonesByOrder,
  type BiweeklyOffsetGroup,
  type BiweeklyTarget,
  type Labeled,
  type StrategyGroup,
} from './clt.mock'

interface BiweeklyOffsetGroupCardProps {
  group: BiweeklyOffsetGroup
  milestones: Labeled[]
  strategyGroups: StrategyGroup[]
  defaultExpanded?: boolean
  onRemove: () => void
}

export function BiweeklyOffsetGroupCard({
  group,
  milestones,
  strategyGroups,
  defaultExpanded = false,
  onRemove,
}: BiweeklyOffsetGroupCardProps) {
  const [expanded, setExpanded] = useState(defaultExpanded)
  const setLabel = useCltWizardStore(s => s.setBiweeklyOffsetGroupLabel)
  const setOffsetDays = useCltWizardStore(
    s => s.setBiweeklyOffsetGroupOffsetDays
  )
  const setMilestoneIds = useCltWizardStore(
    s => s.setBiweeklyOffsetGroupMilestoneIds
  )
  const addTarget = useCltWizardStore(s => s.addBiweeklyTarget)
  const removeTarget = useCltWizardStore(s => s.removeBiweeklyTarget)
  const setTargetStrategyGroup = useCltWizardStore(
    s => s.setBiweeklyTargetStrategyGroup
  )
  const setTargetIsImport = useCltWizardStore(s => s.setBiweeklyTargetIsImport)
  const setTargetIsRail = useCltWizardStore(s => s.setBiweeklyTargetIsRail)

  const milestoneOptions = sortMilestonesByOrder(milestones).map(m => ({
    value: m.id,
    label: m.label,
  }))

  return (
    <div className="rounded-lg border border-border bg-card">
      <button
        type="button"
        onClick={() => setExpanded(v => !v)}
        className="flex w-full cursor-pointer items-center gap-2 px-4 py-3 text-left"
      >
        {expanded ? (
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        )}
        <span className="text-sm font-semibold text-foreground">
          {group.label || 'Untitled biweekly group'}
        </span>
        <span className="text-xs text-muted-foreground">
          · {group.offsetDays}d · {group.milestoneIds.length} milestone
          {group.milestoneIds.length === 1 ? '' : 's'} · {group.targets.length}{' '}
          target{group.targets.length === 1 ? '' : 's'}
        </span>
        <span className="flex-1" />
        <button
          type="button"
          onClick={e => {
            e.stopPropagation()
            onRemove()
          }}
          title="Remove biweekly group"
          className="flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center rounded-md border border-border text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </button>

      {expanded && (
        <div className="space-y-5 border-t border-border px-4 py-4">
          <div className="flex flex-wrap gap-4">
            <div className="min-w-[220px] flex-1 space-y-1.5">
              <Label htmlFor={`bw-label-${group.id}`}>Label</Label>
              <Input
                id={`bw-label-${group.id}`}
                value={group.label}
                placeholder="e.g. SBA 3/4/7 group"
                onChange={e => setLabel(group.id, e.target.value)}
              />
            </div>
            <div className="w-[140px] space-y-1.5">
              <Label htmlFor={`bw-offset-${group.id}`}>Offset (days)</Label>
              <Input
                id={`bw-offset-${group.id}`}
                type="number"
                value={group.offsetDays}
                onChange={e =>
                  setOffsetDays(group.id, Number(e.target.value) || 0)
                }
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Milestones</Label>
            <MultiSelect
              options={milestoneOptions}
              value={group.milestoneIds}
              onValueChange={ids => setMilestoneIds(group.id, ids)}
              placeholder="Select milestones..."
              maxVisibleBadges={4}
            />
            <p className="text-xs text-muted-foreground">
              {group.milestoneIds.length === 0
                ? 'Pick at least one milestone.'
                : `${group.milestoneIds.length} of ${milestoneOptions.length} milestones selected.`}
            </p>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Targets</Label>
              <button
                type="button"
                onClick={() => addTarget(group.id)}
                className="flex shrink-0 cursor-pointer items-center gap-1 rounded-md border border-border bg-background px-2.5 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-muted"
              >
                <Plus className="h-3.5 w-3.5" />
                Add target
              </button>
            </div>
            {group.targets.length === 0 ? (
              <p className="rounded-md border border-dashed border-border bg-background px-3 py-4 text-center text-xs text-muted-foreground">
                No targets yet. Add one to apply this offset to a (strategy
                group + lane) pairing.
              </p>
            ) : (
              <div className="space-y-2">
                {group.targets.map(target => (
                  <TargetRow
                    key={target.id}
                    target={target}
                    strategyGroups={strategyGroups}
                    onStrategyGroupChange={value =>
                      setTargetStrategyGroup(group.id, target.id, value)
                    }
                    onIsImportChange={value =>
                      setTargetIsImport(group.id, target.id, value)
                    }
                    onIsRailChange={value =>
                      setTargetIsRail(group.id, target.id, value)
                    }
                    onRemove={() => removeTarget(group.id, target.id)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function TargetRow({
  target,
  strategyGroups,
  onStrategyGroupChange,
  onIsImportChange,
  onIsRailChange,
  onRemove,
}: {
  target: BiweeklyTarget
  strategyGroups: StrategyGroup[]
  onStrategyGroupChange: (value: string) => void
  onIsImportChange: (value: boolean) => void
  onIsRailChange: (value: boolean) => void
  onRemove: () => void
}) {
  return (
    <div className="flex flex-wrap items-end gap-3 rounded-md border border-border bg-background p-3">
      <div className="min-w-[200px] flex-1 space-y-1">
        <Label className="text-xs text-muted-foreground">Strategy group</Label>
        <Select
          value={target.strategyGroupId || undefined}
          onValueChange={onStrategyGroupChange}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select strategy group..." />
          </SelectTrigger>
          <SelectContent>
            {strategyGroups.map(g => (
              <SelectItem key={g.id} value={g.id}>
                {g.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-1">
        <Label className="text-xs text-muted-foreground">Origin</Label>
        <SegmentedToggle
          options={[
            { label: 'Domestic', value: false },
            { label: 'Import', value: true },
          ]}
          selected={target.isImport}
          onChange={onIsImportChange}
        />
      </div>

      <div className="space-y-1">
        <Label className="text-xs text-muted-foreground">Mode</Label>
        <SegmentedToggle
          options={[
            { label: 'No Rail', value: false },
            { label: 'Rail', value: true },
          ]}
          selected={target.isRail}
          onChange={onIsRailChange}
        />
      </div>

      <button
        type="button"
        onClick={onRemove}
        title="Remove target"
        className="flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-md border border-border text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </div>
  )
}

function SegmentedToggle<T extends boolean>({
  options,
  selected,
  onChange,
}: {
  options: { label: string; value: T }[]
  selected: T
  onChange: (value: T) => void
}) {
  return (
    <div className="inline-flex overflow-hidden rounded-md border border-border">
      {options.map((option, idx) => {
        const isSelected = option.value === selected
        return (
          <button
            key={option.label}
            type="button"
            onClick={() => onChange(option.value)}
            className={cn(
              'cursor-pointer px-3 py-1.5 text-xs font-medium transition-colors',
              idx > 0 && 'border-l border-border',
              isSelected
                ? 'bg-primary text-primary-foreground'
                : 'bg-background text-foreground hover:bg-muted'
            )}
          >
            {option.label}
          </button>
        )
      })}
    </div>
  )
}
